import _ from 'lodash';
import React, { ReactNode, useEffect, useState } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Grid,
	Typography,
	SxProps,
	useTheme,
	useMediaQuery,
	Link,
} from '@mui/material';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';
import investmentKpiLang from '../../langs/investmentKpiLang';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridRowsProp, GridRenderCellParams } from '@mui/x-data-grid';
import investmentTableLang from '../../features/fund-type/langs/investmentTableLang';
import investmentTypeArticleLang from '../../features/fund-type/langs/investmentTypeArticleLang';
import investmentTypeDomainLang from '../../features/fund-type/langs/investmentTypeDomainLang';
import AppFormatPercent from '../../../../app/features/format/AppFormatPercent';
import * as XLSX from 'xlsx';
import CChipFund from '../../components/CChipFund';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import InvestorRoutes from '../../InvestorRoutes';
import { useParams, useNavigate } from 'react-router-dom';
import CAppPaper from '../../../../app/components/CAppPaper';
import Carousel from 'react-material-ui-carousel';
import investmentTypeLang from '../../features/fund-type/langs/investmentTypeLang';
import IFund from '../../interfaces/fund/IFund';
import IInvestor from '../../interfaces/IInvestor';
import styleVariables from '../../../../app/styles/styleVariables';
import { removeAccents } from 'remove-accents-esm';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import CAppDataGrid from '../../../../app/components/CAppDataGrid';
import CAppInfoTooltip from '../../../../app/components/CAppInfoTooltip';
import langInvestWordLang from '../../langs/langInvestWordLang';
import investmentFundStatusLang from '../../features/fund-type/langs/investmentFundStatusLang';
import IInvestorFundStatLine from '../../interfaces/IInvestorFundStatLine';
import IInvestorFund from '../../interfaces/IInvestorFund';
import IInvestorGroup from '../../interfaces/IInvestorGroup';
import moment from 'moment';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';
import useAppLog from '../../../../app/features/log/hooks/useAppLog';
import { langLookup } from '../../../../app/utils/langLookup';

type PortfolioProps = {
	listFunds: IFund[];
	portfolioFunds: IInvestorFundStatLine[] | null;
	portfolioView: string; // 'funds' | 'subportfolio';
	callExportData: boolean;
	resetCallExportData: () => void;
	typeTag: string;
};

interface IResultPortfolioDataDisplay {
	investorId: number;
	groupId: number;
	groupName: string;
	investorName: string;
	fundName: string;
	fundId: number;
	domainName: string;
	sfdr: string;
	vintage?: number | null;
	investmentYear?: number | null;
	isRealized?: boolean | null;
	fundStatus?: string | null;
	percentOwned?: number | null;
	quarter: string;
	currency?: TInvestFundCurrency | null;
	committedCapital?: number | null;
	calledCapital?: number | null;
	callableCapital?: number | null;
	totalDistributed?: number | null;
	totalDistributedMinusInterestIncome?: number | null;
	totalDistributedMinusReturnOfCapital?: number | null;
	netCalledCapital?: number | null;
	netAssetValue?: number | null;
	totalValue?: number | null;
	irr?: number | null;
	irrIsNs?: boolean | null;
	arr?: number | null;
	arrIsNs?: boolean | null;
	dpi?: number | null;
	rvpi?: number | null;
}

type TAccordionMainGroup = {
	id: number;
	isOpen: boolean;
};

type TAccordionSubGroup = {
	id: number;
	isOpen: boolean;
	isOpenDetails: boolean;
};

const VInvestExpertisePortfolio = (props: PortfolioProps) => {
	const { t } = useTranslation();

	const { listFunds, portfolioFunds, portfolioView, typeTag } = props;

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const appLog = useAppLog();

	const [accordionMainGroups, setAccordionMainGroups] = useState<TAccordionMainGroup[] | null>(null);
	const [accordionSubGroups, setAccordionSubGroups] = useState<TAccordionSubGroup[] | null>(null);

	const [funds, setFunds] = useState<IInvestorFund[] | null>(null);
	const [investorGroups, setInvestorGroups] = useState<IInvestorGroup[] | null>(null);
	const [investors, setInvestors] = useState<IInvestor[] | null>(null);

	// Get funds (funds here)
	useEffect(() => {
		if (portfolioFunds) {
			setFunds(
				_.uniqBy(
					portfolioFunds.map((x) => x.fund),
					'id',
				),
			);
		}
	}, [portfolioFunds]);

	// Get investors
	useEffect(() => {
		if (portfolioFunds) {
			setInvestors(
				_.uniqBy(
					portfolioFunds.map((x) => x.investor),
					'id',
				),
			);
		}
	}, [portfolioFunds]);

	// Get investorGroups
	useEffect(() => {
		if (portfolioFunds) {
			setInvestorGroups(
				_.uniqBy(
					portfolioFunds.map((x) => x.investor.group),
					'id',
				),
			);
		}
	}, [portfolioFunds]);

	// Clean date for display
	const [portfolioFundsDisplay, setResultPortfolioDataDisplay] = useState<IResultPortfolioDataDisplay[] | null>(null);
	useEffect(() => {
		if (portfolioFunds && listFunds && appLog) {
			if (portfolioFunds.some((x) => x.reportingPeriod === null && x.reportingDate === null)) {
				appLog.error(
					'no reportingPeriod or reportingDate in portfolioFunds listing in VInvestExpertisePortfolio',
				);
			}
			setResultPortfolioDataDisplay(
				portfolioFunds.map((x): IResultPortfolioDataDisplay => {
					const currentFund = listFunds.find((f) => f.id === x.fund.id) as IFund;
					const stats = x.stats ?? ({} as NonNullable<typeof x.stats>);
					return {
						investorId: x.investor.id,
						groupId: x.investor.group.id,
						groupName: x.investor.group.name,
						investorName: x.investor.name,
						fundName: currentFund.name,
						fundId: currentFund.id,
						domainName:
							currentFund.domainTag && langLookup(investmentTypeDomainLang, currentFund.domainTag)
								? t(langLookup(investmentTypeDomainLang, currentFund.domainTag)?.tag ?? '')
								: '',
						sfdr:
							currentFund.sfdr && langLookup(investmentTypeArticleLang, currentFund.sfdr + '_short')
								? t(langLookup(investmentTypeArticleLang, currentFund.sfdr + '_short')?.tag ?? '')
								: '',
						vintage: currentFund.vintage,
						investmentYear: x.investmentYear,
						isRealized: currentFund.isRealized,
						fundStatus: x.fundStatus,
						percentOwned: stats.percentOwned,
						quarter: x.reportingPeriod
							? x.reportingPeriod.name
							: moment(x.reportingDate).format('DD/MM/YYYY'),
						currency: stats.currency,
						committedCapital: stats.committedCapital,
						calledCapital: stats.calledCapital,
						callableCapital: stats.callableCapital,
						totalDistributed: stats.totalDistributed,
						totalDistributedMinusInterestIncome:
							(stats.totalDistributed ?? -1) >= 0 && (stats.returnOfCapital ?? -1) >= 0
								? _.round((stats.totalDistributed ?? 0) - (stats.returnOfCapital ?? 0), 2)
								: null,

						totalDistributedMinusReturnOfCapital:
							(stats.totalDistributed ?? -1) >= 0 && (stats.interestIncome ?? -1) >= 0
								? _.round((stats.totalDistributed ?? 0) - (stats.interestIncome ?? 0), 2)
								: null,
						netCalledCapital: stats.netCalledCapital,
						netAssetValue: stats.netAssetValue,
						totalValue: stats.totalValue,
						irr: stats.irr,
						irrIsNs: stats.irrIsNs,
						arr: stats.arr,
						arrIsNs: stats.arrIsNs,
						dpi: stats.dpi,
						rvpi: stats.rvpi,
					};
				}),
			);
		}
	}, [appLog, portfolioFunds, listFunds, t]);

	// Init Accordion Group
	useEffect(() => {
		if (investorGroups) {
			setAccordionMainGroups(
				investorGroups.map((g) => ({
					id: g.id,
					isOpen: true,
				})),
			);
		}
	}, [investorGroups]);

	// Init Accordion Investor
	useEffect(() => {
		if (investors) {
			setAccordionSubGroups(
				investors.map((g) => ({
					id: g.id,
					isOpen: true,
					isOpenDetails: false,
				})),
			);
		}
	}, [investors]);

	const [isLoading, setIsLoading] = useState(true);
	useEffect(() => {
		if (
			portfolioFundsDisplay &&
			funds &&
			investorGroups &&
			investors &&
			accordionMainGroups &&
			accordionSubGroups
		) {
			setIsLoading(false);
		}
	}, [portfolioFundsDisplay, investorGroups, investors, accordionMainGroups, funds, accordionSubGroups]);

	useEffect(() => {
		if (props.callExportData && portfolioFundsDisplay) {
			props.resetCallExportData();

			const groupNames = removeAccents(_.uniq((portfolioFundsDisplay ?? []).map((x) => x.groupName)).join(' - '));
			const datesNames = removeAccents(_.uniq((portfolioFundsDisplay ?? []).map((x) => x.quarter)).join(' - '));

			const fileName = `${groupNames} - ${datesNames} - Portfolio.xlsx`;
			const portfolioFundsClean = _.cloneDeep(portfolioFundsDisplay).map((x) => ({
				groupName: x.groupName ?? '',
				investorName: x.investorName ?? '',
				fundName: x.fundName ?? '',
				domainName: x.domainName ?? '',
				sfdr: x.sfdr ?? '',
				vintage: x.vintage ?? '',
				investmentYear: x.investmentYear ?? '',
				fundStatus:
					x.fundStatus && langLookup(investmentFundStatusLang, x.fundStatus)
						? t(langLookup(investmentFundStatusLang, x.fundStatus)?.tag ?? '')
						: '',
				percentOwned: x.percentOwned ?? t(langInvestWordLang.nc.tag),
				quarter: x.quarter ?? '',
				currency: x.currency ?? '',
				committedCapital: x.committedCapital ?? t(langInvestWordLang.nc.tag),
				calledCapital: x.calledCapital ?? t(langInvestWordLang.nc.tag),
				callableCapital: x.callableCapital ?? t(langInvestWordLang.nc.tag),
				totalDistributed: x.totalDistributed ?? t(langInvestWordLang.nc.tag),
				totalDistributedMinusInterestIncome:
					x.totalDistributedMinusInterestIncome ?? t(langInvestWordLang.nc.tag),
				totalDistributedMinusReturnOfCapital:
					x.totalDistributedMinusReturnOfCapital ?? t(langInvestWordLang.nc.tag),
				netCalledCapital: x.netCalledCapital ?? t(langInvestWordLang.nc.tag),
				netAssetValue: x.netAssetValue ?? t(langInvestWordLang.nc.tag),
				totalValue: x.totalValue ?? t(langInvestWordLang.nc.tag),
				irr: x.irrIsNs ? t(langInvestWordLang.ns.tag) : x.irr ?? t(langInvestWordLang.nc.tag),
				arr: x.arrIsNs ? t(langInvestWordLang.ns.tag) : x.arr ?? t(langInvestWordLang.nc.tag),
				dpi: x.dpi ?? t(langInvestWordLang.nc.tag),
				rvpi: x.rvpi ?? t(langInvestWordLang.nc.tag),
			}));

			const headers = [
				t(investmentTableLang.group.tag),
				t(investmentTableLang.investor.tag),
				t(investmentTableLang.fund.tag),
				t(investmentTableLang.domain.tag),
				t(investmentTableLang.sfdr.tag),
				t(investmentTableLang.vintage.tag),
				t(investmentTableLang.investment_year.tag),
				t(investmentTableLang.status.tag),
				t(investmentTableLang.percent_owned_for_export.tag),
				t(investmentTableLang.reporting_period.tag),
				t(investmentTableLang.fund_currency.tag),
				t(investmentTableLang.committed_capital.tag),
				t(investmentTableLang.called_capital.tag),
				t(investmentTableLang.callable_capital.tag),
				t(investmentTableLang.total_distributed.tag),
				t(investmentTableLang.total_distributed_minus_interest_income.tag),
				t(investmentTableLang.total_distributed_minus_return_of_capital.tag),
				t(investmentTableLang.net_called_capital.tag),
				t(investmentTableLang.net_asset_value.tag),
				t(investmentTableLang.total_value.tag),
				t(investmentTableLang.irr.tag),
				t(investmentTableLang.arr.tag),
				t(investmentTableLang.dpi.tag),
				t(investmentTableLang.rvpi.tag),
			];

			const worksheet = XLSX.utils.json_to_sheet(portfolioFundsClean);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Portfolio');

			worksheet['!cols'] = _.keys(portfolioFundsClean[0]).map((field, index) => {
				const header = headers[index] ?? '';
				return {
					wch: Math.max(
						header.length,
						Math.max(
							...portfolioFundsClean.map(
								(obj: Record<string, unknown>) => String(obj[field] ?? '').length,
							),
						),
					),
				};
			});

			XLSX.writeFileXLSX(workbook, fileName);
		}
	}, [props.callExportData, portfolioFundsDisplay, props, t]);

	const handleToggleMainGroup = (mainGroup: TAccordionMainGroup) => {
		if (!accordionMainGroups) return;
		setAccordionMainGroups(
			accordionMainGroups.map((x) => ({
				...x,
				isOpen: x.id === mainGroup.id ? !x.isOpen : x.isOpen,
			})),
		);
	};

	const handleToggleSubGroup = (subGroupId: number) => {
		if (!accordionSubGroups) return;
		setAccordionSubGroups(
			accordionSubGroups.map((x) => ({
				...x,
				isOpen: x.id === subGroupId ? !x.isOpen : x.isOpen,
			})),
		);
	};

	const handleToggleSubGroupFundDetails = (subGroupId: number) => {
		if (!accordionSubGroups) return;
		setAccordionSubGroups(
			accordionSubGroups.map((x) => ({
				...x,
				isOpenDetails: x.id === subGroupId ? !x.isOpenDetails : x.isOpenDetails,
			})),
		);
	};

	const sxHeaderMainGroup = {
		'.group_name': {
			fontSize: '1.125rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#FFF',
		},
		'.kpi_label': {
			fontSize: '0.875rem',
			fontWeight: '400',
			lineHeight: '1.5rem',
			color: '#FFF',
		},
		'.kpi_value': {
			fontSize: '1rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#2ABDBB',
		},
	} as SxProps;

	const sxHeaderSubGroup = {
		'.group_name': {
			fontSize: '1.125rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#1A2B48',
		},
		'.kpi_label': {
			fontSize: '0.875rem',
			fontWeight: '400',
			lineHeight: '1.5rem',
			color: '#1A2B48',
		},
		'.kpi_value': {
			fontSize: '0.9rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#2ABDBB',
		},
	} as SxProps;

	const sxAccordionSummaryMainGroup =
		theme.palette.mode === 'dark'
			? {
					backgroundColor: '#1A2B48',
					border: '2px solid white',
			  }
			: {
					backgroundColor: '#1A2B48',
					border: '2px solid #1A2B48',
			  };

	const sxAccordionSummarySubGroup =
		theme.palette.mode === 'dark'
			? {
					color: 'white',
					border: '1px solid white',
					backgroundColor: '#1A2B48',
					'& svg': {
						color: 'white',
					},
					'& .group_name': {
						color: '#FFF!important',
					},
					'& .kpi_label': {
						color: '#FFF',
					},
			  }
			: {
					backgroundColor: '#FFF',
					border: '1px solid #FFF',
					'& svg': {
						color: '#1A2B48',
					},
			  };

	return (
		<>
			{!isLoading ? (
				<Box id="expertise-content-portfolio">
					{(investorGroups ?? []).map((group) => {
						const fundsDisplay = (portfolioFundsDisplay ?? []).filter((x) => x.groupId === group.id);
						const currencies = _.uniqBy(fundsDisplay, 'currency').map((x) => x['currency']);
						const defaultCurrency = currencies.length === 1 ? currencies[0] : null;
						return (
							<Accordion
								key={group.id}
								expanded={
									isMobile
										? true
										: (accordionMainGroups ?? []).some((g) => g.id === group.id && g.isOpen)
								}
								sx={{
									...sxHeaderMainGroup,
									background: 'none',
									boxShadow: 'none',
									borderRadius: '10px',
								}}
							>
								<AccordionSummary
									expandIcon={
										isMobile ? null : (
											<AppCustomIcon
												tag="chevron_up"
												invColor
												sx={{
													marginLeft: '12px',
													marginRight: '12px',
												}}
											/>
										)
									}
									sx={{
										color: 'white',
										borderRadius: '10px',
										...sxAccordionSummaryMainGroup,
									}}
									onClick={() => handleToggleMainGroup({ id: group.id, isOpen: true })}
								>
									<Grid
										container
										spacing={1}
										alignItems="stretch"
										sx={{ marginRight: { sm: '36px', sx: '0px' } }}
									>
										<Grid item sm={3}>
											<Typography className="group_name">{group.name}</Typography>
										</Grid>
										<Grid item container spacing={1} xs={12} sm={9} alignItems="stretch">
											<Grid
												item
												container
												spacing={3}
												alignItems="stretch"
												sm={4}
												sx={{ textAlign: { sm: 'right', xs: 'left' } }}
											>
												<Grid item xs={8}>
													<Typography className="kpi_label">
														{t(investmentKpiLang.committed_capital.tag)}{' '}
													</Typography>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													<Typography
														className="kpi_value"
														sx={{
															transform: { sm: 'translate(-10%)' },
															width: { sm: 'calc(100% + 10%)' },
														}}
													>
														<AppFormatAmount
															value={_.sumBy(fundsDisplay, 'committedCapital')}
															addSpace
															currency={defaultCurrency}
														/>
													</Typography>
												</Grid>
											</Grid>
											<Grid
												item
												container
												spacing={3}
												alignItems="stretch"
												sm={4}
												sx={{ textAlign: { sm: 'right', xs: 'left' } }}
											>
												<Grid item xs={8}>
													<Typography className="kpi_label">
														{t(investmentKpiLang.called_capital.tag)}{' '}
													</Typography>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													<Typography
														className="kpi_value"
														sx={{
															transform: { sm: 'translate(-10%)' },
															width: { sm: 'calc(100% + 10%)' },
														}}
													>
														<AppFormatAmount
															value={_.sumBy(fundsDisplay, 'calledCapital')}
															addSpace
															currency={defaultCurrency}
														/>
													</Typography>
												</Grid>
											</Grid>
											<Grid
												item
												container
												spacing={3}
												alignItems="stretch"
												sm={4}
												sx={{ textAlign: { sm: 'right', xs: 'left' } }}
											>
												<Grid item xs={8}>
													<Box style={{ height: '100%' }}>
														<Typography
															className="kpi_label"
															sx={{
																transform: { sm: 'translate(-10%)' },
															}}
														>
															{t(investmentKpiLang.total_value.tag)}{' '}
														</Typography>
													</Box>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													<Box style={{ height: '100%' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: { sm: 'translate(-10%)' },
																width: { sm: 'calc(100% + 10%)' },
															}}
														>
															{fundsDisplay.some((x) => x.totalValue === null) ? (
																t(langInvestWordLang.nc.tag)
															) : (
																<AppFormatAmount
																	value={_.sumBy(fundsDisplay, 'totalValue')}
																	addSpace
																	currency={defaultCurrency}
																/>
															)}
														</Typography>
													</Box>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</AccordionSummary>
								<AccordionDetails
									sx={{
										padding: { sm: '15px 0px 15px 20px', xs: '15px 0px' },
									}}
								>
									{portfolioView === 'funds' ? (
										<ViewGroupBySubPortfolio
											portfolioView={portfolioView}
											investors={investors}
											group={group}
											accordionSubGroups={accordionSubGroups}
											isMobile={isMobile}
											sxAccordionSummarySubGroup={sxAccordionSummarySubGroup}
											sxHeaderSubGroup={sxHeaderSubGroup}
											handleToggleSubGroup={handleToggleSubGroup}
											portfolioFundsDisplay={portfolioFundsDisplay}
											handleToggleSubGroupFundDetails={handleToggleSubGroupFundDetails}
										/>
									) : (
										<ViewGroupByFund
											portfolioView={portfolioView}
											group={group}
											isMobile={isMobile}
											sxAccordionSummarySubGroup={sxAccordionSummarySubGroup}
											sxHeaderSubGroup={sxHeaderSubGroup}
											portfolioFunds={portfolioFunds ?? []}
											portfolioFundsDisplay={portfolioFundsDisplay}
											typeTag={typeTag}
										/>
									)}
								</AccordionDetails>
							</Accordion>
						);
					})}
				</Box>
			) : (
				''
			)}
		</>
	);
};

type ViewGroupByFundProps = {
	portfolioView: string;
	group: IInvestorGroup;
	isMobile: boolean;
	sxAccordionSummarySubGroup: SxProps;
	sxHeaderSubGroup: SxProps;
	portfolioFunds: IInvestorFundStatLine[];
	portfolioFundsDisplay: IResultPortfolioDataDisplay[] | null;
	typeTag: string;
};

const ViewGroupByFund = (props: ViewGroupByFundProps) => {
	const {
		portfolioView,
		group,
		isMobile,
		sxAccordionSummarySubGroup,
		sxHeaderSubGroup,
		portfolioFunds,
		portfolioFundsDisplay,
		typeTag,
	} = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const navigate = useNavigate();

	const [accordionSubGroups, setAccordionSubGroups] = useState<
		| {
				id: number;
				isOpen: boolean;
				isOpenDetails: boolean;
		  }[]
		| null
	>(null);

	// Init Accordion Investor
	useEffect(() => {
		if (portfolioFunds) {
			setAccordionSubGroups(
				_.uniq(portfolioFunds.filter((x) => x.investor.group.id === group.id).map((x) => x.fund.id)).map(
					(x: number) => {
						return {
							id: x,
							isOpen: true,
							isOpenDetails: false,
						};
					},
				),
			);
		}
	}, [portfolioFunds, group]);

	const handleToggleSubGroup = (subGroupId: number) => {
		if (!accordionSubGroups) return;
		setAccordionSubGroups(
			accordionSubGroups.map((x) => ({
				...x,
				isOpen: x.id === subGroupId ? !x.isOpen : x.isOpen,
			})),
		);
	};

	const handleToggleSubGroupDetails = (subGroupId: number) => {
		if (!accordionSubGroups) return;
		setAccordionSubGroups(
			accordionSubGroups.map((x) => ({
				...x,
				isOpenDetails: x.id === subGroupId ? !x.isOpenDetails : x.isOpenDetails,
			})),
		);
	};

	return (
		<>
			{accordionSubGroups &&
				accordionSubGroups.map((accordionSubGroup, index) => {
					const portfolioFund = portfolioFunds.find((x) => x.fund.id === accordionSubGroup.id);
					if (!portfolioFund) return null;
					const fundsDisplay = (portfolioFundsDisplay ?? []).filter(
						(x) => x.groupId === group.id && x.fundId === portfolioFund.fund.id,
					);
					const currencies = _.uniqBy(fundsDisplay, 'currency').map((x) => x['currency']);
					const defaultCurrency = currencies.length === 1 ? currencies[0] : null;
					if (portfolioFund) {
						return (
							<Box
								key={index}
								sx={{
									marginBottom: '15px',
									opacity: portfolioFund.fundStatus === 'liquidated' ? '0.6' : '1',
								}}
							>
								<Accordion
									expanded={
										isMobile
											? true
											: (accordionSubGroups ?? []).some(
													(i) => i.id === portfolioFund.fund.id && i.isOpen,
											  )
									}
									sx={{
										...sxHeaderSubGroup,
										background: 'transparent',
										boxShadow: 'none',
										borderRadius: '10px',
									}}
								>
									<AccordionSummary
										expandIcon={
											isMobile ? null : (
												<AppCustomIcon
													tag="chevron_up"
													sx={{
														marginLeft: '12px',
														marginRight: '12px',
													}}
												/>
											)
										}
										sx={{
											borderRadius: '10px',
											...sxAccordionSummarySubGroup,
											'&.Mui-expanded': {
												borderRadius: '10px 10px 0px 0px',
											},
										}}
										onClick={() => handleToggleSubGroup(portfolioFund.fund.id)}
									>
										<Grid
											container
											spacing={1}
											alignItems="stretch"
											sx={{ marginRight: { sm: '36px', xs: '0px' } }}
										>
											<Grid
												item
												sm={3}
												onClick={(event) => event.stopPropagation()}
												onFocus={(event) => event.stopPropagation()}
											>
												<Typography
													className="group_name"
													onClick={() =>
														navigate(
															HAppRoutes.getPath(InvestorRoutes.fund.tag, {
																investExpertiseTag: typeTag,
																id: portfolioFund.fund?.id.toString(),
															}),
														)
													}
												>
													{portfolioFund.fund.name}
												</Typography>
											</Grid>
											<Grid item container spacing={1} xs={12} sm={9} alignItems="stretch">
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Typography
															className="kpi_label"
															sx={{
																transform: {
																	sm: 'translate(-5.5%)',
																},
															}}
														>
															{t(investmentKpiLang.committed_capital.tag)}{' '}
														</Typography>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: {
																	sm: 'translate(-12%)',
																},
															}}
														>
															<AppFormatAmount
																value={_.sumBy(fundsDisplay, 'committedCapital')}
																addSpace
																currency={defaultCurrency}
															/>
														</Typography>
													</Grid>
												</Grid>
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Typography
															className="kpi_label"
															sx={{
																transform: {
																	sm: 'translate(-3.5%)',
																},
															}}
														>
															{t(investmentKpiLang.called_capital.tag)}{' '}
														</Typography>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: { sm: 'translate(-7%)' },
															}}
														>
															<AppFormatAmount
																value={_.sumBy(fundsDisplay, 'calledCapital')}
																addSpace
																currency={defaultCurrency}
															/>
														</Typography>
													</Grid>
												</Grid>
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Box style={{ height: '100%' }}>
															<Typography
																className="kpi_label"
																sx={{
																	transform: {
																		sm: 'translate(-11%)',
																	},
																}}
															>
																{t(investmentKpiLang.total_value.tag)}{' '}
															</Typography>
														</Box>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Box style={{ height: '100%' }}>
															<Typography
																className="kpi_value"
																sx={{
																	transform: {
																		sm: 'translate(-10%)',
																	},
																}}
															>
																{fundsDisplay.some((x) => x.totalValue === null) ? (
																	t(langInvestWordLang.nc.tag)
																) : (
																	<AppFormatAmount
																		value={_.sumBy(fundsDisplay, 'totalValue')}
																		addSpace
																		currency={defaultCurrency}
																	/>
																)}
															</Typography>
														</Box>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</AccordionSummary>
									<AccordionDetails
										sx={{
											border: '1px solid white',
											marginBottom: '15px',
											paddingBottom: '0px',
											borderBottomLeftRadius: '10px',
											borderBottomRightRadius: '10px',
											backgroundColor: theme.palette.mode === 'dark' ? '#1A2B48' : '#FFF',
										}}
									>
										{!isMobile ? (
											<TableFunds
												portfolioView={portfolioView}
												data={(portfolioFundsDisplay ?? []).filter(
													(x) => x.groupId === group.id && x.fundId === portfolioFund.fund.id,
												)}
											/>
										) : (
											<Link
												color="primary"
												sx={{
													position: 'relative',
													fontWeight: 600,
													fontSize: '0.875rem',
													lineHeight: '1.7',
												}}
												onClick={() => handleToggleSubGroupDetails(portfolioFund.fund.id)}
											>
												{t(investmentTypeLang.get_x_subportfolios_details.tag, {
													count: (portfolioFundsDisplay ?? []).filter(
														(f) => f.fundId === portfolioFund.fund.id,
													).length,
												})}

												{(accordionSubGroups ?? []).some(
													(i) => i.id === portfolioFund.fund.id && i.isOpenDetails,
												) ? (
													<KeyboardArrowUpOutlined
														sx={{
															marginTop: '0px',
															position: 'absolute',
															top: '50%',
															transform: 'translateY(-50%)',
														}}
													/>
												) : (
													<KeyboardArrowDownOutlined
														sx={{
															marginTop: '0px',
															position: 'absolute',
															top: '50%',
															transform: 'translateY(-50%)',
														}}
													/>
												)}
											</Link>
										)}
									</AccordionDetails>
								</Accordion>
								{isMobile &&
								(accordionSubGroups ?? []).some(
									(i) => i.id === portfolioFund.fund.id && i.isOpenDetails,
								) ? (
									<BoxFunds
										view="subportfolio"
										data={(portfolioFundsDisplay ?? []).filter(
											(f) => f.fundId === portfolioFund.fund.id,
										)}
									/>
								) : (
									''
								)}
							</Box>
						);
					}
				})}
		</>
	);
};

type ViewGroupBySubPortfolioProps = {
	portfolioView: string;
	group: IInvestorGroup;
	accordionSubGroups: TAccordionSubGroup[] | null;
	handleToggleSubGroup: (subGroupId: number) => void;
	handleToggleSubGroupFundDetails: (subGroupId: number) => void;
	investors: IInvestor[] | null;
	isMobile: boolean;
	sxAccordionSummarySubGroup: SxProps;
	sxHeaderSubGroup: SxProps;
	portfolioFundsDisplay: IResultPortfolioDataDisplay[] | null;
};

const ViewGroupBySubPortfolio = (props: ViewGroupBySubPortfolioProps) => {
	const {
		portfolioView,
		investors,
		group,
		accordionSubGroups,
		isMobile,
		sxAccordionSummarySubGroup,
		sxHeaderSubGroup,
		handleToggleSubGroup,
		portfolioFundsDisplay,
		handleToggleSubGroupFundDetails,
	} = props;
	const { t } = useTranslation();
	const theme = useTheme();
	return (
		<>
			{(investors ?? [])
				.filter((i) => i.group.id === group.id)
				.map((investor) => {
					const fundsDisplay = (portfolioFundsDisplay ?? []).filter((x) => x.investorId === investor.id);
					const currencies = _.uniqBy(fundsDisplay, 'currency').map((x) => x['currency']);
					const defaultCurrency = currencies.length === 1 ? currencies[0] : null;
					return (
						<Box key={investor.id} sx={{ marginBottom: '15px' }}>
							<Accordion
								expanded={
									isMobile
										? true
										: (accordionSubGroups ?? []).some((i) => i.id === investor.id && i.isOpen)
								}
								sx={{
									...sxHeaderSubGroup,
									background: 'transparent',
									boxShadow: 'none',
									borderRadius: '10px',
								}}
							>
								<AccordionSummary
									expandIcon={
										isMobile ? null : (
											<AppCustomIcon
												tag="chevron_up"
												sx={{
													marginLeft: '12px',
													marginRight: '12px',
												}}
											/>
										)
									}
									sx={{
										borderRadius: '10px',
										...sxAccordionSummarySubGroup,
										'&.Mui-expanded': {
											borderRadius: '10px 10px 0px 0px',
										},
									}}
									onClick={() => handleToggleSubGroup(investor.id)}
								>
									<Grid
										container
										spacing={1}
										alignItems="stretch"
										sx={{ marginRight: { sm: '36px', xs: '0px' } }}
									>
										<Grid item sm={3}>
											<Typography className="group_name">{investor.name}</Typography>
										</Grid>
										<Grid item container spacing={1} xs={12} sm={9} alignItems="stretch">
											<Grid
												item
												container
												spacing={3}
												alignItems="stretch"
												sm={4}
												sx={{ textAlign: { sm: 'right', xs: 'left' } }}
											>
												<Grid item xs={8}>
													<Typography
														className="kpi_label"
														sx={{
															transform: {
																sm: 'translate(-5.5%)',
															},
														}}
													>
														{t(investmentKpiLang.committed_capital.tag)}{' '}
													</Typography>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													<Typography
														className="kpi_value"
														sx={{
															transform: {
																sm: 'translate(-12%)',
															},
														}}
													>
														<AppFormatAmount
															value={_.sumBy(fundsDisplay, 'committedCapital')}
															addSpace
															currency={defaultCurrency}
														/>
													</Typography>
												</Grid>
											</Grid>
											<Grid
												item
												container
												spacing={3}
												alignItems="stretch"
												sm={4}
												sx={{ textAlign: { sm: 'right', xs: 'left' } }}
											>
												<Grid item xs={8}>
													<Typography
														className="kpi_label"
														sx={{
															transform: {
																sm: 'translate(-3.5%)',
															},
														}}
													>
														{t(investmentKpiLang.called_capital.tag)}{' '}
													</Typography>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													<Typography
														className="kpi_value"
														sx={{
															transform: { sm: 'translate(-7%)' },
														}}
													>
														<AppFormatAmount
															value={_.sumBy(fundsDisplay, 'calledCapital')}
															addSpace
															currency={defaultCurrency}
														/>
													</Typography>
												</Grid>
											</Grid>
											<Grid
												item
												container
												spacing={3}
												alignItems="stretch"
												sm={4}
												sx={{ textAlign: { sm: 'right', xs: 'left' } }}
											>
												<Grid item xs={8}>
													<Box style={{ height: '100%' }}>
														<Typography
															className="kpi_label"
															sx={{
																transform: {
																	sm: 'translate(-11%)',
																},
															}}
														>
															{t(investmentKpiLang.total_value.tag)}{' '}
														</Typography>
													</Box>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													<Box style={{ height: '100%' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: {
																	sm: 'translate(-10%)',
																},
															}}
														>
															{fundsDisplay.some((x) => x.totalValue === null) ? (
																t(langInvestWordLang.nc.tag)
															) : (
																<AppFormatAmount
																	value={_.sumBy(fundsDisplay, 'totalValue')}
																	addSpace
																	currency={defaultCurrency}
																/>
															)}
														</Typography>
													</Box>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</AccordionSummary>
								<AccordionDetails
									sx={{
										border: '1px solid white',
										marginBottom: '15px',
										paddingBottom: '0px',
										borderBottomLeftRadius: '10px',
										borderBottomRightRadius: '10px',
										backgroundColor: theme.palette.mode === 'dark' ? '#1A2B48' : '#FFF',
									}}
								>
									{!isMobile ? (
										<TableFunds
											portfolioView={portfolioView}
											data={(portfolioFundsDisplay ?? []).filter(
												(f) => f.investorId === investor.id,
											)}
										/>
									) : (
										<Link
											color="primary"
											sx={{
												position: 'relative',
												fontWeight: 600,
												fontSize: '0.875rem',
												lineHeight: '1.7',
											}}
											onClick={() => handleToggleSubGroupFundDetails(investor.id)}
										>
											{t(investmentTypeLang.get_x_funds_details.tag, {
												count: (portfolioFundsDisplay ?? []).filter(
													(f) => f.investorId === investor.id,
												).length,
											})}

											{(accordionSubGroups ?? []).some(
												(i) => i.id === investor.id && i.isOpenDetails,
											) ? (
												<KeyboardArrowUpOutlined
													sx={{
														marginTop: '0px',
														position: 'absolute',
														top: '50%',
														transform: 'translateY(-50%)',
													}}
												/>
											) : (
												<KeyboardArrowDownOutlined
													sx={{
														marginTop: '0px',
														position: 'absolute',
														top: '50%',
														transform: 'translateY(-50%)',
													}}
												/>
											)}
										</Link>
									)}
								</AccordionDetails>
							</Accordion>
							{isMobile &&
							(accordionSubGroups ?? []).some((i) => i.id === investor.id && i.isOpenDetails) ? (
								<BoxFunds
									view="funds"
									data={(portfolioFundsDisplay ?? []).filter((f) => f.investorId === investor.id)}
								/>
							) : (
								''
							)}
						</Box>
					);
				})}
		</>
	);
};

type TableFundsProps = {
	data: IResultPortfolioDataDisplay[];
	portfolioView: string;
};

const TableFunds = (props: TableFundsProps) => {
	const { data, portfolioView } = props;

	const routeParams = useParams();
	const navigate = useNavigate();

	const { t } = useTranslation();

	const [rows, setRows] = useState<GridRowsProp | null>(null);

	useEffect(() => {
		if (data) {
			setRows(
				_.orderBy(
					data.map((x, index) => {
						return {
							...x,
							id: index,
						};
					}),
					['isRealized'],
					['asc'],
				),
			);
		}
	}, [data]);

	const columns: GridColDef[] = [
		{
			field: 'investorName',
			headerName: t(investmentTableLang.investor.tag),
			minWidth: 250,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			hide: portfolioView === 'funds',
		},
		{
			field: 'fundName',
			headerName: t(investmentTableLang.fund.tag),
			minWidth: 250,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<CChipFund
						displayTooltip
						onClick={() =>
							navigate(
								HAppRoutes.getPath(InvestorRoutes.fund.tag, {
									investExpertiseTag: routeParams.investExpertiseTag,
									id: params.row.fundId.toString(),
								}),
							)
						}
						disabled={params.row.isRealized}
						label={params.value}
					/>
				);
			},
			hide: portfolioView !== 'funds',
		},
		{
			field: 'domainName',
			headerName: t(investmentTableLang.domain.tag),
			width: 170,
			align: 'left',
			headerAlign: 'left',
			hide: portfolioView !== 'funds',
		},
		{
			field: 'sfdr',
			headerName: t(investmentTableLang.sfdr.tag),
			width: 60,
			align: 'center',
			headerAlign: 'center',
			hide: portfolioView !== 'funds',
		},
		{
			field: 'vintage',
			headerName: t(investmentTableLang.vintage.tag),
			width: 90,
			align: 'center',
			headerAlign: 'center',
			hide: portfolioView !== 'funds',
		},
		{
			field: 'investmentYear',
			headerName: t(investmentTableLang.investment_year.tag),
			width: 120,
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'fundStatus',
			headerName: t(investmentTableLang.status.tag),
			width: 90,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => {
				return t(langLookup(investmentFundStatusLang, params.value)?.tag ?? '');
			},
		},
		{
			field: 'committedCapital',
			headerName: t(investmentTableLang.committed_capital.tag),
			width: 120,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatAmount
						value={params.value}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={params.row.currency}
					/>
				);
			},
		},

		{
			field: 'calledCapital',
			headerName: t(investmentTableLang.called_capital.tag),
			width: 100,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatAmount
						value={params.value}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={params.row.currency}
					/>
				);
			},
		},
		{
			field: 'percentOwned',
			headerName: t(investmentTableLang.percent_owned.tag),
			width: 160,
			align: 'right',
			headerAlign: 'right',
			disableColumnMenu: true,
			disableReorder: true,
			disableExport: true,
			renderHeader: () => (
				<div className="MuiDataGrid-columnHeaderTitle">
					{t(investmentTableLang.percent_owned.tag)}
					<CAppInfoTooltip title={t(investmentTableLang.percent_owned_tooltip.tag)} />
				</div>
			),
			renderCell: (params: GridRenderCellParams) => {
				return <AppFormatPercent value={params.value} textIfNull={t(langInvestWordLang.nc.tag)} />;
			},
		},
		{
			field: 'totalValue',
			headerName: t(investmentTableLang.total_value.tag),
			width: 110,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatAmount
						value={params.value}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={params.row.currency}
					/>
				);
			},
		},
		{
			field: 'quarter',
			headerName: t(investmentTableLang.nav_calc.tag),
			width: 100,
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'irr',
			headerName: t(investmentTableLang.irr.tag),
			width: 60,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatPercent
						value={params.value}
						isNS={params.row.irrIsNs}
						textIfNull={t(langInvestWordLang.nc.tag)}
					/>
				);
			},
		},
	];

	const theme = useTheme();

	return (
		<>
			{rows ? (
				<Box>
					<CAppDataGrid
						sx={{
							marginLeft: '-10px',
							'*::-webkit-scrollbar': {
								height: '5px',
							},
							'*::-webkit-scrollbar-track': {
								backgroundColor: theme.palette.background.paper,
							},

							'*::-webkit-scrollbar-thumb': {
								backgroundColor: theme.palette.primary.main,
							},
						}}
						getRowClassName={(params) =>
							portfolioView === 'funds' ? `row-status--${params.row.isRealized}` : ''
						}
						rows={rows}
						disableSelectionOnClick
						disableColumnSelector
						disableColumnMenu
						columns={columns}
						autoHeight
						hideFooter
					/>
				</Box>
			) : (
				''
			)}
		</>
	);
};

type BoxFundsProps = {
	data: IResultPortfolioDataDisplay[];
	view: string;
};

const BoxFunds = (props: BoxFundsProps) => {
	const { data, view } = props;

	const routeParams = useParams();
	const navigate = useNavigate();

	const { t } = useTranslation();
	const theme = useTheme();

	const [rows, setRows] = useState<GridRowsProp | null>(null);

	useEffect(() => {
		if (data) {
			setRows(
				data.map((x, index) => {
					return {
						...x,
						id: index,
					};
				}),
			);
		}
	}, [data]);

	return (
		<>
			{rows ? (
				<Box>
					<Carousel swipe indicators animation="fade" autoPlay={false}>
						{rows.map((row) => {
							return (
								<CAppPaper
									key={row.id}
									variant="outlined"
									sx={{
										backgroundColor:
											theme.palette.mode === 'dark' ? styleVariables.primaryColor : '#F4F9FF',
										marginBottom: '30px',
										border: '1px solid white',
									}}
								>
									<Grid container spacing={3}>
										{view === 'subportfolio' ? (
											<Grid item xs={12}>
												<BoxFundItem
													label={t(investmentTableLang.investor.tag)}
													value={row.investorName}
												/>
											</Grid>
										) : (
											<Grid item xs={12}>
												<BoxFundItem
													label={t(investmentTableLang.fund.tag)}
													value={
														<CChipFund
															displayTooltip
															onClick={() =>
																navigate(
																	HAppRoutes.getPath(InvestorRoutes.fund.tag, {
																		investExpertiseTag:
																			routeParams.investExpertiseTag,
																		id: row.fundId.toString(),
																	}),
																)
															}
															disabled={row.isRealized}
															label={row.fundName}
														/>
													}
												/>
											</Grid>
										)}
										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.domain.tag)}
												value={row.domainName}
											/>
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem label={t(investmentTableLang.sfdr.tag)} value={row.sfdr} />
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.vintage.tag)}
												value={row.vintage}
											/>
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.investment_year.tag)}
												value={row.investmentYear}
											/>
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.status.tag)}
												value={t(
													langLookup(investmentFundStatusLang, row.fundStatus)?.tag ?? '',
												)}
											/>
										</Grid>

										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.committed_capital.tag)}
												value={
													<AppFormatAmount
														value={row.committedCapital}
														textIfNull={t(langInvestWordLang.nc.tag)}
														currency={row.currency}
													/>
												}
											/>
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem
												label={
													<>
														{t(investmentTableLang.percent_owned.tag)}{' '}
														<CAppInfoTooltip
															title={t(investmentTableLang.percent_owned_tooltip.tag)}
														/>
													</>
												}
												value={
													<AppFormatPercent
														value={row.percentOwned}
														textIfNull={t(langInvestWordLang.nc.tag)}
													/>
												}
											/>
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.called_capital.tag)}
												value={
													<AppFormatAmount
														value={row.calledCapital}
														textIfNull={t(langInvestWordLang.nc.tag)}
														currency={row.currency}
													/>
												}
											/>
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.total_value.tag)}
												value={
													<AppFormatAmount
														value={row.totalValue}
														textIfNull={t(langInvestWordLang.nc.tag)}
														currency={row.currency}
													/>
												}
											/>
										</Grid>
										<Grid item xs={6}>
											<BoxFundItem
												label={t(investmentTableLang.irr.tag)}
												value={
													<AppFormatPercent
														value={row.irr}
														textIfNull={t(investmentKpiLang.ns_value.tag)}
													/>
												}
											/>
										</Grid>
									</Grid>
								</CAppPaper>
							);
						})}
					</Carousel>
				</Box>
			) : (
				''
			)}
		</>
	);
};

type BoxFundItemProps = {
	label: string | ReactNode;
	value: string | number | ReactNode;
};
export const BoxFundItem = (props: BoxFundItemProps) => {
	const theme = useTheme();
	const sxItem = {
		'.label': {
			fontWeight: 600,
			fontSize: '0.82rem',
			lineHeight: '1.8',
			color: theme.palette.mode === 'dark' ? '#FFF' : '#6F8098',
		},
		'.value': {
			fontWeight: 400,
			fontSize: '0.9rem',
			lineHeight: '1.5',
			color: theme.palette.mode === 'dark' ? '#FFF' : theme.palette.primary.main,
		},
	} as SxProps;
	return (
		<Box sx={sxItem}>
			<Box className="label">{props.label}</Box>
			<Box className="value">{props.value}</Box>
		</Box>
	);
};

export default VInvestExpertisePortfolio;
